import React, {ReactNode, ReactElement, useState} from 'react';
import {cx} from '@emotion/css';
import {useIntl} from 'react-intl';
import {useCookies} from 'react-cookie';
import Button, {ButtonVariant} from '@fo/designSystem/Buttons/Button/Button';
import {CloseIcon} from '@fo/root/designSystem/atoms/Icons';
import {IconSize} from '@fo/root/designSystem/atoms/Icons/IconWrapper';
import {cookieDomain, hasSecureCookie} from '@fo/shared-types/constants';
import getBooleanFromString from '@fo/shared-getters/getBooleanFromString';
import {track, segmentEvents} from '@fo/shared-analytics/tracking/segment';

import styles from './DialogueCard.styles';

interface DialogueCardProps {
  name?: string;
  children?: ReactNode;
  className?: string;
  innerClassName?: string;
  isHideCloseButton?: boolean;
  onClose?: () => void;
  closeButtonContents?: ReactNode;
  preventDialogueAutoClosing?: boolean;
  cookieName?: string;
  testId?: string;
}

function DialogueCard({
  name,
  children,
  className,
  innerClassName,
  isHideCloseButton,
  onClose,
  closeButtonContents,
  preventDialogueAutoClosing = false,
  cookieName,
  testId,
}: DialogueCardProps): ReactElement | null {
  const [cookies, setCookie] = useCookies(cookieName ? [cookieName] : []);

  const isClosed = cookieName && getBooleanFromString(cookies[cookieName]);

  const [isVisible, setIsVisible] = useState<boolean>(!isClosed);
  const {formatMessage} = useIntl();

  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    if (preventDialogueAutoClosing) {
      return;
    }

    setIsVisible(false);

    // Segment tracking
    track(segmentEvents.actions.dismissibleDialogDismissed, {...(name ? {dialogueName: name} : {})});

    if (cookieName) {
      setCookie(cookieName, true, {
        path: '/',
        domain: cookieDomain,
        secure: hasSecureCookie,
        maxAge: 60 * 60 * 24 * 7,
      });
    }
  };

  return (
    <div className={cx(styles.container, className)} data-testid={testId}>
      <div className={cx(styles.content, innerClassName)}>{children}</div>
      {!isHideCloseButton && (
        <Button
          onClick={handleClose}
          variant={ButtonVariant.UNSTYLED}
          ariaLabel={formatMessage({id: 'dialogueCard.close'})}
          className={cx(styles.closeBase, !closeButtonContents && styles.closeDefaultExtras)}
          testId={`button.dialogueCard.dismiss${name ? `-${name}` : ''}`}
        >
          {closeButtonContents || <CloseIcon size={IconSize.EXTRA_SMALL} />}
        </Button>
      )}
    </div>
  );
}

export default DialogueCard;
